import { lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import { useQuery } from '@apollo/client';
import get from 'lodash/get';
import {
  ROLE_PERMISSIONS,
  useDefaultPermissions,
} from '../api/permissions/queries';
import { Routes, Route, Navigate } from 'react-router-dom';
import { string } from 'prop-types';

import * as paths from '../config/paths';

MainRoutes.propTypes = {
  userRole: string,
};

const Search = lazy(() => import('../features/Search'));
const Rogbot = lazy(() => import('./Rogbot'));
const Tools = lazy(() => import('../features/Tools/Tools'));
const PartnerNew = lazy(() => import('../features/PartnerNew/PartnerNew'));
const Partner = lazy(() => import('../features/Partner/Partner'));
const Partners = lazy(() => import('./Partners/Partners'));
const Skyhook = lazy(() => import('../features/Skyhook'));
const OrganizationNew = lazy(() =>
  import('../features/OrganizationNew/OrganizationNew')
);
const Location = lazy(() => import('../features/Location/Location'));
const Integrations = lazy(() =>
  import('../features/Integrations/Integrations')
);
const Automations = lazy(() =>
  import('../features/Automations/screens/Automations/index.jsx')
);
const AutomationsConfigureTemplate = lazy(() =>
  import('../features/Automations/screens/ConfigureTemplate')
);
const Marketplace = lazy(() =>
  import('../features/Marketplace/screens/Marketplace/index.jsx')
);
const MarketplaceNewApp = lazy(() =>
  import('../features/Marketplace/screens/NewApp/index.jsx')
);
const MarketplaceApp = lazy(() =>
  import('../features/Marketplace/screens/EditApp/index.jsx')
);
const MarketplaceUpdateDataMapping = lazy(() =>
  import('../features/Marketplace/screens/UpdateDataMap/index.jsx')
);
const MarketplaceDataMappings = lazy(() =>
  import('../features/Marketplace/screens/DataMappings/index.jsx')
);
const AutomationsConfigureSolution = lazy(() =>
  import('../features/Automations/screens/ConfigureSolution')
);
const AutomationsListSolutions = lazy(() =>
  import('../features/Automations/screens/ListSolutions')
);
const MarketplaceAssociateApps = lazy(() =>
  import('../features/Marketplace/screens/AssociateApps/index.jsx')
);
const Organization = lazy(() =>
  import('../features/Organization/Organization')
);
const Favorites = lazy(() => import('../features/Favorites/Favorites'));
const Logout = lazy(() => import('./Logout'));

function MainRoutes() {
  const { data } = useQuery(ROLE_PERMISSIONS);
  const { defaultPermissions, loadingDefaultPermissions } =
    useDefaultPermissions();

  const marketplacePermission = loadingDefaultPermissions
    ? false
    : defaultPermissions.some(
        permission =>
          permission?.slug === 'skyhook_can_access_marketplace' &&
          permission?.authorized
      );

  const automationsPermission = loadingDefaultPermissions
    ? false
    : defaultPermissions.some(
        permission =>
          permission?.slug === 'skyhook_can_access_automations' &&
          permission?.authorized
      );

  const userRole = get(data, 'rolePermission[0].role', '');

  const canAccessRogbot =
    userRole === 'supportReps' || userRole !== 'canUseRogbot' ? null : (
      <Route
        path="rogbot/*"
        element={<Rogbot canUseRogbot={userRole === 'canUseRogbot'} />}
      />
    );

  const canAccessIntegrations =
    userRole === 'supportReps' ? null : (
      <Route path="integrations/*" element={<Integrations />} />
    );

  const canAccessPartners =
    userRole === 'supportReps' ? null : (
      <Route path="partners" element={<Partners />} />
    );

  const canAccessAutomations = automationsPermission ? (
    <Route path={paths.AUTOMATIONS} element={<Automations />} />
  ) : null;

  const canManageAutomationTemplates = automationsPermission ? (
    <>
      <Route
        path={paths.AUTOMATIONS_ADD_TEMPLATE}
        element={<AutomationsConfigureTemplate />}
      />
      <Route
        path={paths.AUTOMATIONS_EDIT_TEMPLATE}
        element={<AutomationsConfigureTemplate />}
      />
    </>
  ) : null;

  const canAccessMarketplace = marketplacePermission ? (
    <Route path={paths.MARKETPLACE} element={<Marketplace />} />
  ) : null;

  const canAddNewAppToMarketplace = marketplacePermission ? (
    <Route path={paths.MARKETPLACE_NEW_APP} element={<MarketplaceNewApp />} />
  ) : null;

  const canSeeMarketplaceDataMapping = marketplacePermission ? (
    <>
      <Route
        path={paths.MARKETPLACE_APP_UPDATE_DATA_MAPPING}
        element={<MarketplaceUpdateDataMapping />}
      />
      <Route
        path={paths.MARKETPLACE_APP_DATA_MAPPINGS}
        element={<MarketplaceDataMappings />}
      />
    </>
  ) : null;

  const canManageSolutions = marketplacePermission ? (
    <>
      <Route
        path={paths.AUTOMATIONS_ADD_SOLUTION}
        element={<AutomationsConfigureSolution />}
      />
      <Route
        path={paths.AUTOMATIONS_EDIT_SOLUTION}
        element={<AutomationsConfigureSolution />}
      />
      <Route
        path={paths.AUTOMATIONS_LIST_SOLUTIONS}
        element={<AutomationsListSolutions />}
      />
    </>
  ) : null;
  const canAddEditAppToMarketplace = marketplacePermission ? (
    <Route path={paths.MARKETPLACE_APP} element={<MarketplaceApp />} />
  ) : null;

  const canAssociateAppsMarketplace = marketplacePermission ? (
    <Route
      path={paths.MARKETPLACE_ASSOCIATE_APPS}
      element={<MarketplaceAssociateApps />}
    />
  ) : null;

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route index path="/" element={<Skyhook />} />
        <Route path="search" element={<Search />} />
        <Route
          path="organization/:organizationUid/location/:locationUid/*"
          element={<Location />}
        />
        <Route
          path="organization/:organizationUid/*"
          element={<Organization />}
        />
        <Route path="organizationNew" element={<OrganizationNew />} />
        {canAccessRogbot}
        <Route path="tools/*" element={<Tools />} />
        {canAccessPartners}
        <Route path="partner/:partnerUid/*" element={<Partner />} />
        <Route path="partnerNew" element={<PartnerNew />} />
        {canAccessIntegrations}
        {canAccessMarketplace}
        {canAccessAutomations}
        {canManageAutomationTemplates}
        {canAddNewAppToMarketplace}
        {canAddEditAppToMarketplace}
        {canManageSolutions}
        {canAssociateAppsMarketplace}
        {canSeeMarketplaceDataMapping}
        <Route path="favorites" element={<Favorites />} />
        <Route path="logout" element={<Logout />} />
        <Route path="/*" element={<Navigate to={paths.HOME} />} />
      </Routes>
    </Suspense>
  );
}

function mapStateToProps({ permissions }) {
  return {
    userRole: permissions.userRole,
  };
}

export default connect(mapStateToProps)(MainRoutes);
